import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Zap, ArrowRight } from 'lucide-react';
import { authFetch } from '../../lib/auth';

/* Customer 360 — CRM Evolution phase 1. Slide-over card built from every native signal. */

const TYPE_ICON = { lead: '●', pain: '💬', nudge: '↩', demo: '🎬', contact: '🙋', bridge: '🤝', email: '✉', brevo: '🔁' };

export function Customer360Drawer({ email, isEs, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!email) return;
    setD(null);
    authFetch(`/os2/customer360?email=${encodeURIComponent(email)}`).then((r) => r.json()).then(setD).catch(() => setD({ error: true }));
  }, [email]);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  if (!email) return null;
  const lead = d?.lead || {};
  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || (d?.demo?.name) || email.split('@')[0];
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(isEs ? 'es' : 'en', { day: '2-digit', month: 'short', year: '2-digit' }) : '');
  const scoreColor = (s) => (s >= 70 ? '#059669' : s >= 40 ? '#F77F11' : '#9ca3af');
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[95] bg-black/45" onClick={onClose} data-testid="c360-overlay">
        <motion.div initial={{ x: 60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ type: 'tween', duration: 0.22 }}
          onClick={(e) => e.stopPropagation()} data-testid="c360-drawer"
          className="absolute right-0 top-0 h-full w-full sm:w-[440px] bg-white shadow-2xl overflow-y-auto">
          <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-gray-100 px-5 py-4 flex items-center gap-3 z-10">
            <span className="w-10 h-10 shrink-0 rounded-full bg-[#FFF7ED] border border-orange-200 text-[#F77F11] text-[15px] font-bold flex items-center justify-center uppercase">{name.slice(0, 1)}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[14.5px] font-bold text-gray-900 truncate" data-testid="c360-name">{name}{lead.company && <span className="text-gray-500 font-medium"> · {lead.company}</span>}</p>
              <p className="text-[11.5px] text-gray-400 truncate">{email}</p>
            </div>
            <button onClick={onClose} data-testid="c360-close" className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"><X className="w-4 h-4" /></button>
          </div>

          {!d ? <p className="p-6 text-[13px] text-gray-400">…</p> : (
            <div className="p-5 space-y-4">
              <div className="flex flex-wrap gap-1">
                {lead.source && <span className="rounded bg-orange-50 text-[#d8651a] px-1.5 py-0.5 text-[9.5px] font-bold uppercase">{lead.source}</span>}
                {lead.locale && <span className="rounded bg-gray-100 text-gray-500 px-1.5 py-0.5 text-[9.5px] font-bold uppercase">{lead.locale}</span>}
                {(lead.metadata?.market || lead.geo?.country_code) && <span className="rounded bg-indigo-50 text-indigo-500 px-1.5 py-0.5 text-[9.5px] font-bold uppercase">{lead.metadata?.market || lead.geo?.country_code}</span>}
                {lead.metadata?.ref && <span className="rounded bg-orange-50 text-[#d8651a] px-1.5 py-0.5 text-[9.5px] font-bold uppercase">via {lead.metadata.ref}</span>}
                {lead.created_at && <span className="rounded bg-gray-100 text-gray-400 px-1.5 py-0.5 text-[9.5px] font-semibold">{isEs ? 'desde' : 'since'} {fmt(lead.created_at)}</span>}
              </div>

              <div className="grid grid-cols-3 gap-2" data-testid="c360-stats">
                <div className="rounded-2xl border border-gray-100 p-3 text-center">
                  <p className="text-[22px] font-black tabular-nums leading-none" style={{ color: scoreColor(d.score) }}>{d.score}</p>
                  <p className="mt-1 text-[9.5px] uppercase tracking-wider font-bold text-gray-400">AI Score</p>
                </div>
                <div className="rounded-2xl border border-gray-100 p-3 text-center">
                  <p className="text-[22px] font-black tabular-nums leading-none text-gray-900">{d.emails.sent}<span className="text-[12px] text-gray-400 font-bold"> ✉</span></p>
                  <p className="mt-1 text-[9.5px] uppercase tracking-wider font-bold text-gray-400">{d.emails.open_rate}% {isEs ? 'abiertos' : 'opened'}</p>
                </div>
                <div className="rounded-2xl border border-gray-100 p-3 text-center">
                  <p className="text-[15px] font-black leading-none pt-1.5">{d.demo ? (d.demo.verified ? '🎬 ✓' : '🎬 …') : '—'}</p>
                  <p className="mt-1.5 text-[9.5px] uppercase tracking-wider font-bold text-gray-400">Demo</p>
                </div>
              </div>

              <div className="rounded-2xl bg-[#121418] text-white p-4" data-testid="c360-rachel">
                <p className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.18em] font-bold text-[#F77F11]"><Zap className="w-3 h-3" /> {isEs ? 'Recomendación de Rachel' : 'Rachel recommendation'}</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-white/90">{isEs ? d.rachel_es : d.rachel_en}</p>
                {!!d.score_why?.length && <p className="mt-2 text-[10.5px] text-white/45">{isEs ? 'Señales:' : 'Signals:'} {d.score_why.join(' · ')}</p>}
              </div>

              {!!d.pains?.length && (
                <div data-testid="c360-pains">
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-400 mb-1.5">{isEs ? 'Sus dolores (Welcome Gate)' : 'Their pains (Welcome Gate)'}</p>
                  {d.pains.slice(0, 3).map((p, i) => (
                    <p key={i} className="border-l-2 border-[#F77F11] pl-3 py-1 text-[12.5px] italic text-gray-600 mb-1.5">
                      “{p.message || p.category}” <span className="not-italic text-[10px] font-bold text-[#d8651a] uppercase">{p.category}</span>
                    </p>
                  ))}
                </div>
              )}

              {!!(lead.brevo?.lists || []).length && (
                <div data-testid="c360-brevo">
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-400 mb-1.5">Brevo · {isEs ? 'listas sincronizadas' : 'synced lists'}</p>
                  <p className="flex flex-wrap gap-1">
                    {lead.brevo.lists.map((l) => (
                      <span key={l} className="rounded bg-emerald-50 border border-emerald-200 text-emerald-700 px-1.5 py-0.5 text-[9.5px] font-bold">{l}</span>
                    ))}
                    {lead.brevo.email_blacklisted && (
                      <span className="rounded bg-red-50 border border-red-200 text-red-600 px-1.5 py-0.5 text-[9.5px] font-bold uppercase">{isEs ? 'no contactar' : 'blacklisted'}</span>
                    )}
                  </p>
                </div>
              )}

              {d.demo && (
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-[12px] text-gray-600 space-y-0.5" data-testid="c360-demo">
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-400 mb-1">Demo</p>
                  {[['restaurant_name', isEs ? 'Restaurante' : 'Restaurant'], ['city', isEs ? 'Ciudad' : 'City'], ['pos', 'POS']].map(([k, l]) => d.demo[k] && <p key={k}><b>{l}:</b> {d.demo[k]}</p>)}
                  {!!(d.demo.goals || []).length && <p><b>{isEs ? 'Objetivos' : 'Goals'}:</b> {d.demo.goals.join(', ')}</p>}
                </div>
              )}

              <div data-testid="c360-timeline">
                <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-400 mb-1.5">{isEs ? 'Línea de tiempo' : 'Timeline'}</p>
                {(d.timeline || []).map((t, i) => (
                  <div key={i} className="flex items-start gap-2.5 py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-[12px] w-5 text-center shrink-0">{TYPE_ICON[t.type] || '·'}</span>
                    <p className="text-[12px] text-gray-600 leading-snug flex-1">{isEs ? t.label_es : t.label_en}</p>
                    <span className="text-[10px] text-gray-300 tabular-nums shrink-0">{fmt(t.at)}</span>
                  </div>
                ))}
                {!d.timeline?.length && <p className="text-[12px] text-gray-400">{isEs ? 'Sin actividad registrada' : 'No recorded activity'}</p>}
              </div>

              <div className="flex items-center gap-3 pb-2">
                <a href={`mailto:${email}`} className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 text-white px-4 py-2 text-[12px] font-bold hover:bg-gray-700 transition-colors" data-testid="c360-email-btn"><Mail className="w-3.5 h-3.5" /> Email</a>
                <a href="/admin/ai-os/pipeline?tab=sales" className="inline-flex items-center gap-1 text-[12px] font-bold text-[#F77F11] hover:underline" data-testid="c360-sales-link">Sales Intelligence <ArrowRight className="w-3.5 h-3.5" /></a>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
